import { Observable } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../config/config.service';
import { BackendUrlConfig } from '../config/backend-url-config';

/**
 * Abstract base class for making HTTP requests against one of the backends.
 *
 * There is no default backend: every subclass names the one it talks to by returning the matching
 * URL from {@link backendUrls}, so the target is visible in the service itself instead of depending
 * on how the hosting app happens to be wired.
 *
 * ```ts
 * protected override get baseUrl(): string {
 *   return this.backendUrls.psServerUrl;
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export abstract class ApiService {
  protected httpClient = inject(HttpClient);
  private readonly backendUrlConfigService = inject(ConfigService<BackendUrlConfig>);

  /**
   * The backend this service sends its requests to. Pick one of {@link backendUrls}.
   */
  protected abstract get baseUrl(): string;

  /**
   * Backend URLs as configured in the app's `assets/config/config.json`.
   */
  protected get backendUrls(): BackendUrlConfig {
    return this.backendUrlConfigService.config;
  }

  /**
   * Specifies a suffix which is attached to the base url on every request.
   * Override this getter to specify it.
   */
  protected get baseUrlSuffix(): string {
    return '';
  }

  protected get<R>(path: string | number, options: object | undefined = undefined): Observable<R> {
    return this.httpClient.get<R>(this.getUrl(String(path)), options);
  }

  protected put<R, D>(path: string, data?: D): Observable<R> {
    return this.httpClient.put<R>(this.getUrl(path), data);
  }

  protected post<R, D>(path: string, data?: D, options?: object): Observable<R> {
    return this.httpClient.post<R>(this.getUrl(path), data, options);
  }

  protected delete<T>(path: string, options?: object): Observable<T> {
    return this.httpClient.delete<T>(this.getUrl(path), options);
  }

  protected patch<R, D>(path: string, data: D): Observable<R> {
    return this.httpClient.patch<R>(this.getUrl(path), data);
  }

  /**
   * Constructs the full URL for the given API path.
   *
   * @param path The API endpoint path
   * @returns The full URL string
   */
  private getUrl(path: string | number): string {
    return `${this.baseUrl}${this.baseUrlSuffix}/${path}`;
  }
}
